import React from "react";
import NewGetListPaginatedTable from "../../../components/Pagination/GetListPaginatedTable.tsx";
import { Col, Container } from "../../../components/Theme/grid.tsx";
import CopyToClipboard from "../../../components/utils/CopyToClipboard.tsx";
import { formatDate } from "../../../components/utils/HighlightNullAndOldDates.tsx";
import {
  type IssueWithAssetRegister,
  issueCategoriesEnum,
  useGetIssuesIssueGet,
} from "../../../kubb";
import ManualIssueCreateModal from "../ManualIssue/ManualIssueCreateModal.tsx";
import { IssueCancelledBadge } from "./IssueCancelledBadge.tsx";
import { IssueOpenBadge } from "./IssueOpenBadge.tsx";
import { IssueSeverityBadge } from "./IssueSeverityBadge.tsx";
import { issueCategoryFormatter } from "./model.tsx";

interface IssuesListProps {
  companyId?: string;
  schemeId?: string;
  assetPositionId?: string;
  includeCancelled?: boolean;
  includeClosed?: boolean;
}

function IssueListRow(issue: IssueWithAssetRegister): React.ReactElement {
  const issueShouldHaveStartDate =
    issue.category !== issueCategoriesEnum["MANUAL.NOT_ADOPTED"];
  return (
    <tr key={issue.issue_id}>
      <td className={"flex gap-1"}>
        <span>{issueCategoryFormatter(issue)}</span>
        <IssueCancelledBadge issue={issue} />
        <IssueOpenBadge issue={issue} />
      </td>
      <td>
        {issueShouldHaveStartDate ? (
          <>
            {formatDate(issue.start_date_time)}
            {issue.end_date_time
              ? ` to ${formatDate(issue.end_date_time)}`
              : " and ongoing"}
          </>
        ) : (
          <>
            {issue.end_date_time
              ? `Until ${formatDate(issue.end_date_time)}`
              : "Ongoing"}
          </>
        )}
      </td>
      <td>
        <IssueSeverityBadge issue={issue} />
      </td>
      <td>
        <CopyToClipboard value={issue.issue_id} slice={8} iconSize={"sm"} />
      </td>
      <td>{formatDate(issue.raised_date_time)}</td>
    </tr>
  );
}

export default function IssuesList({
  companyId,
  schemeId,
  assetPositionId,
  includeCancelled = false,
  includeClosed = false,
}: IssuesListProps): React.ReactElement {
  const header = (
    <tr>
      <th scope="col" className="text-left">
        Description
      </th>
      <th scope="col" className="text-left">
        Dates affected
      </th>
      <th scope="col" className="text-left">
        Severity
      </th>
      <th scope="col" className="text-left">
        Issue ID
      </th>
      <th scope="col" className="text-left">
        Created at
      </th>
    </tr>
  );

  const [lastUpdateRequested, setLastUpdateRequested] = React.useState(
    new Date(),
  );

  return (
    <Container>
      {companyId && assetPositionId && (
        <Col sm={12}>
          <div className={"flex flex-row-reverse"}>
            <ManualIssueCreateModal
              companyId={companyId}
              assetPositionId={assetPositionId}
              onComplete={() => {
                setLastUpdateRequested(new Date());
                return Promise.resolve();
              }}
            />
          </div>
        </Col>
      )}
      <Col sm={12}>
        <NewGetListPaginatedTable
          params={{
            company_id: companyId,
            scheme_id: schemeId,
            asset_position_id: assetPositionId,
            include_cancelled: includeCancelled,
            include_closed: includeClosed,
          }}
          cursor={{ max_items: 25 }}
          row={IssueListRow}
          header={[header, 5]}
          tableClassName={"list-group-flush"}
          data-cy={"issue-list"}
          lastUpdateRequested={lastUpdateRequested}
          useQuery={useGetIssuesIssueGet}
        />
      </Col>
    </Container>
  );
}
